import { useSyncExternalStore } from "react";

import { containsDomainError } from "#shared/infra/apiError";
import {
  type ImpactedOptimization,
  OPTIMIZATION_CONFIRMATION_REQUIRED,
  type OptimizationInvalidationOperation,
} from "shared";

class OptimizationInvalidationCancelledError extends Error {
  constructor() {
    super("The user kept the applied optimizations and cancelled the change");
    this.name = "OptimizationInvalidationCancelledError";
  }
}

export const isOptimizationInvalidationCancelled = (error: unknown): boolean =>
  error instanceof OptimizationInvalidationCancelledError;

type PendingOptimizationInvalidation = {
  optimizations: ImpactedOptimization[];
  operation: OptimizationInvalidationOperation;
  resolvers: Array<(confirmed: boolean) => void>;
};

let pending: PendingOptimizationInvalidation | null = null;
let preapprovedIds = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => pending;

const mergeById = (current: ImpactedOptimization[], added: ImpactedOptimization[]) => {
  const byId = new Map(current.map((optimization) => [optimization.id, optimization]));
  added.forEach((optimization) => byId.set(optimization.id, optimization));
  return [...byId.values()];
};

const UPDATE_OPERATION: OptimizationInvalidationOperation = { type: "UPDATE" };

const mergeOperation = (
  current: OptimizationInvalidationOperation | undefined,
  added: OptimizationInvalidationOperation,
) => (current?.type === "DELETE" ? current : added);

export const requestOptimizationInvalidationConfirmation = (
  optimizations: ImpactedOptimization[],
  operation: OptimizationInvalidationOperation = UPDATE_OPERATION,
) =>
  new Promise<boolean>((resolve) => {
    pending = {
      optimizations: mergeById(pending?.optimizations ?? [], optimizations),
      operation: mergeOperation(pending?.operation, operation),
      resolvers: [...(pending?.resolvers ?? []), resolve],
    };
    notify();
  });

export const settleOptimizationInvalidation = (confirmed: boolean) => {
  const settled = pending;
  pending = null;
  notify();
  settled?.resolvers.forEach((resolve) => resolve(confirmed));
};

export const usePendingOptimizationInvalidation = () =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

const getRequiredOptimizationConfirmation = (
  status: number,
  body: unknown,
): {
  optimizations: ImpactedOptimization[];
  operation?: OptimizationInvalidationOperation;
} | null => {
  if (status !== OPTIMIZATION_CONFIRMATION_REQUIRED.http || !containsDomainError(body)) {
    return null;
  }
  const { code, params } = body.domainException;
  if (code !== OPTIMIZATION_CONFIRMATION_REQUIRED.code) {
    return null;
  }
  return params as {
    optimizations: ImpactedOptimization[];
    operation?: OptimizationInvalidationOperation;
  };
};

export const preapproveOptimizationInvalidation = (optimizationIds: string[]) => {
  preapprovedIds = new Set(optimizationIds);
};

const consumePreapproval = (optimizations: ImpactedOptimization[]) => {
  const isPreapproved =
    preapprovedIds.size > 0 &&
    optimizations.every((optimization) => preapprovedIds.has(optimization.id));
  preapprovedIds = new Set();
  return isPreapproved;
};

export const confirmOptimizationInvalidation = async (
  status: number,
  body: unknown,
): Promise<boolean> => {
  const required = getRequiredOptimizationConfirmation(status, body);
  if (!required) {
    return false;
  }
  if (consumePreapproval(required.optimizations)) {
    return true;
  }
  const confirmed = await requestOptimizationInvalidationConfirmation(
    required.optimizations,
    required.operation,
  );
  if (!confirmed) {
    throw new OptimizationInvalidationCancelledError();
  }
  return true;
};
